import { TaxReturn } from "./types";
import { BoxFile } from "@modules/box/types";

/**
 * Gets the current tax year.
 * Tax year is the previous calendar year (e.g., in 2026, tax year is 2025)
 * @returns The current tax year as a string
 */
export const getCurrentTaxYear = (): string => {
  const currentYear = new Date().getFullYear();
  return (currentYear - 1).toString();
};

/**
 * Sorts tax returns by type, with individual returns first, then business returns.
 * @param returns - Array of tax returns to sort
 * @returns Sorted array with individual returns before business returns
 */
export const sortReturnsByType = (returns: TaxReturn[]): TaxReturn[] => {
  return returns.sort((a, b) => {
    if (a.TYPE === "individual" && b.TYPE === "business") return -1;
    if (a.TYPE === "business" && b.TYPE === "individual") return 1;
    return 0;
  });
};

/**
 * Formats a tax return into a display label.
 * Format: "{YEAR} {Personal|Business} return {NAME}"
 * Example: "2025 Personal return John Doe"
 * @param taxReturn - The tax return to format
 * @returns Formatted label string
 */
export const formatReturnLabel = (taxReturn: TaxReturn): string => {
  const type = taxReturn.TYPE === "individual" ? "Personal" : "Business";
  return `${taxReturn.YEAR} ${type} Return: ${taxReturn.NAME}`;
};

/**
 * Filters uploaded files to return only those associated with a specific tax return.
 * @param uploadedFiles - Array of Box files to filter
 * @param taxReturn - The tax return to match documents against
 * @returns Array of Box files that are associated with the tax return
 */
export const getReturnDocuments = (
  uploadedFiles: BoxFile[] | undefined,
  taxReturn: TaxReturn | undefined
): BoxFile[] => {
  if (!uploadedFiles || !taxReturn) return [];

  return uploadedFiles.filter((file) => {
    return taxReturn.DOCUMENTS?.some((doc) => doc.DOCUMENT_ID === file.id);
  });
};
